/**
 * detection-common.js
 */


import { Types } from "../../../utils/constants";


export const sectionTypes = {
  ENVIRONMENT: 1,
  ODOR_ENVIRONMENT: 2,
  ODOR: 3
};


// default environment
export const defaultEnvironment = (options) => ({ 
  id: '',
  type: { id: options?.recentDetectionValues?.environmentType?.id ?? '' }, 
  count: '' 
})


// default odor-environment
export const defaultOdorEnvironment = (options) => ({
  id: '',
  name: '',
  description: '',
  vehicle: {
    type: '',
    make: '',
    model: '',
    color: '',
    countryId: options?.recentCommonValues?.location?.country?.id || '',
    regionId: options?.recentCommonValues?.location?.region?.id || '',
  }
})


// default odor
export const defaultOdor = (options, recordType) => {
  const recent = options?.recentDetectionValues;
  const defaultOdorType = {
    [Types.Record.EXERCISE]: Types.OdorPrimary.PROOFING,
    [Types.Record.DEPLOYMENT]: Types.OdorPrimary.ALCOHOL, // really?
  }[recordType];

  const type = recent?.odorType ?? { id: defaultOdorType };
  const subType = (type.id === Types.OdorPrimary.PROOFING && recent?.odorPackaging)
    ? recent.odorPackaging
    : type.id === recent?.odorType?.id && recent?.odorSubType
      ? recent.odorSubType
      : getOdorSubTypeOptions(options, type.id)[0];
  const quantityUnit = getDefaultOdorUnit(options, type.id);
  const packaging = recent?.odorPackaging ?? { id: '', name: '' };

  return {
    id: '',
    type,
    subType,
    quantity: '',
    quantityUnit,
    packaging,
    packaging2: { id: '', name: '' },
    packaging3: { id: '', name: '' },
    packaging4: { id: '', name: '' },
    concealedLocation: '',
    description: '',
    height: '',
    depth: '',
    photo: null,
  };
}


// gets list of odor sub-type options based on given odor type
export const getOdorSubTypeOptions = (options, odorTypeId) => {
  return options
    .odorSubTypes
    .find(ot => ot.id === odorTypeId)
    .subTypes;
}


// gets list of odor quantity options based on given odor type
export const getOdorQuantityUnitOptions = (options, odorTypeId) => {
  return  options
    .odorTypeUnits
    .find(ot => ot.id === odorTypeId)
    .units;
}


// gets the default odor quantity selection based on given odor type
export const getDefaultOdorUnit = (options, odorTypeId) => {
  const unitOptions = getOdorQuantityUnitOptions(options, odorTypeId);

  switch (odorTypeId) {
    case Types.OdorPrimary.PROOFING:
    case Types.OdorPrimary.DRUG_PARAPHERNALIA:
    case Types.OdorPrimary.ELECTRONICS:
    case Types.OdorPrimary.FIREARMS:
      return unitOptions.find(o => o.name === 'Items');
    case Types.OdorPrimary.ALCOHOL:
    case Types.OdorPrimary.ARSON:
      return unitOptions.find(o => o.name === 'Fluid Ounces');
    case Types.OdorPrimary.CADAVER:
      return unitOptions.find(o => o.name === 'Ounces');
    case Types.OdorPrimary.CONCEALED_HUMANS:
      return unitOptions.find(o => o.name === 'Humans');
    case Types.OdorPrimary.CURRENCY:
      return unitOptions.find(o => o.name === 'USD');
    case Types.OdorPrimary.DRUGS:
    case Types.OdorPrimary.EXPLOSIVES:
      return unitOptions.find(o => o.name === 'Grams');
    case Types.OdorPrimary.WILDLIFE:
      return unitOptions.find(o => o.name === 'Animals');
    default:
      return unitOptions[0];
  }
}


// generates array of all environment ids in the detection structure
export const getEnvironmentIds = (detectionEnvironments) => {
  return detectionEnvironments?.map(env => env.type.id) || [];
}

// returns true if there is an odor environment with no odours
export const odorlessEnvironmentExists = (detectionEnvironments) => {
  return Boolean(
    detectionEnvironments?.find(env => 
      Boolean(env.odorEnvironments?.length) 
      && env.odorEnvironments.find(odorEnv => !Boolean(odorEnv.odors?.length))));
}


// generates array of all odor ids in the detection structure
export const getOdorIds = (detectionEnvironments) => {
  const allOdorIds = detectionEnvironments?.map(env => 
    env?.odorEnvironments?.map(oenv => 
      oenv?.odors?.map(odor => odor.type.id)))
    .reduce((total, a) => a !== undefined ? [...total, ...a] : total, [])
    .reduce((total, a) => a !== undefined ? [...total, ...a] : total, []);
      
  return [...new Set(allOdorIds)];
}


// updates form default values with given values at object position defined by formNamePrefix.
// used to save given values to the form's default values when clicking 'done' so we can reset if they cancel.
export const updateDefaultToCurrent = (formContext, formNamePrefix, values) => {
  formContext.resetField(formNamePrefix, { defaultValue: values, keepDirty: true });
}